import { z } from 'zod';
import { SiteSchema } from './site.js';

/**
 * User Roles
 */
export const UserRoleSchema = z.enum(['viewer', 'editor', 'supervisor', 'admin']);
export type UserRole = z.infer<typeof UserRoleSchema>;

export const GlobalRoleSchema = z.enum(['superadmin']);
export type GlobalRole = z.infer<typeof GlobalRoleSchema>;

export const GlobalRolesSchema = z.array(GlobalRoleSchema).superRefine((roles, ctx) => {
  const uniqueRoles = new Set(roles);
  if (uniqueRoles.size !== roles.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Global roles must not contain duplicates',
    });
  }
});
export type GlobalRoles = z.infer<typeof GlobalRolesSchema>;

/**
 * Authentication Providers
 */
// NOTE: 'legacy' represents a non-login placeholder identity used to preserve historical
// attribution during migrations (no Entra ID / local credentials).
export const AuthProviderSchema = z.enum(['m365', 'local', 'kiosk', 'legacy']);
export type AuthProvider = z.infer<typeof AuthProviderSchema>;

interface ProviderBoundaryUserShape {
  authProvider: AuthProvider;
  m365ObjectId?: string | null;
  m365Upn?: string | null;
}

function applyProviderBoundaryRules(
  user: ProviderBoundaryUserShape,
  ctx: z.RefinementCtx,
  options?: {
    disallowedM365Credential?: {
      present: boolean | string | null | undefined;
      path: 'password' | 'passwordHash';
      message: string;
    };
  },
) {
  if (user.authProvider === 'm365') {
    if (!user.m365ObjectId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Microsoft 365 users must include m365ObjectId',
        path: ['m365ObjectId'],
      });
    }
    if (!user.m365Upn) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Microsoft 365 users must include m365Upn',
        path: ['m365Upn'],
      });
    }
    if (options?.disallowedM365Credential?.present) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: options.disallowedM365Credential.message,
        path: [options.disallowedM365Credential.path],
      });
    }
  }

  if (user.authProvider === 'local') {
    if (user.m365ObjectId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Local users must not include m365ObjectId',
        path: ['m365ObjectId'],
      });
    }
    if (user.m365Upn) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Local users must not include m365Upn',
        path: ['m365Upn'],
      });
    }
  }
}

/**
 * User Schema
 *
 * Represents a user account with authentication and authorization metadata.
 * Supports local password authentication with lockout enforcement.
 */
export const UserSchema = z
  .object({
    id: z.string().uuid(),
    site: SiteSchema.optional(),
    areaId: z.string().uuid().nullable().optional(),
    username: z.string().min(1, 'Username is required'),
    displayName: z.string().min(1, 'Display name is required'),
    email: z.string().email('Invalid email').nullable().optional(),
    m365ObjectId: z.string().uuid().nullable().optional(),
    m365Upn: z.string().email('Invalid email').nullable().optional(),
    role: UserRoleSchema,
    globalRoles: GlobalRolesSchema.optional(),
    authProvider: AuthProviderSchema,
    canLogin: z.boolean().default(true),
    legacyIdentityRaw: z.string().nullable().optional(),
    locale: z.string().default('de-CH'), // e.g., 'de-CH', 'en-US', 'fr-CH'
    timeZone: z.string().default('Europe/Zurich'),
    active: z.boolean().default(true),

    // Password & Security Fields
    passwordHash: z.string().nullable().optional(), // Only for authProvider='local'
    failedLoginCount: z.number().int().nonnegative().default(0),
    lockedUntil: z.string().datetime().nullable().optional(), // ISO 8601 datetime
    lastLoginAt: z.string().datetime().nullable().optional(),

    // Audit metadata
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .superRefine((user, ctx) => {
    // 'legacy' users are placeholders for migrated/orphaned identities and must not be able to log in.
    if (user.authProvider === 'legacy' && user.canLogin !== false) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Legacy users must have canLogin=false',
        path: ['canLogin'],
      });
    }

    applyProviderBoundaryRules(user, ctx, {
      disallowedM365Credential: {
        present: user.passwordHash,
        path: 'passwordHash',
        message: 'Microsoft 365 users must not store passwordHash',
      },
    });
  });

export type User = z.infer<typeof UserSchema>;

/**
 * Password Requirements Schema
 *
 * Enforces strong password policy:
 * - Minimum 12 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one digit
 * - At least one special character (@$!%*?&#)
 */
export const PasswordRequirementsSchema = z
  .string()
  .min(12, 'Password must be at least 12 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one digit')
  .regex(/[@$!%*?&#]/, 'Password must contain at least one special character (@$!%*?&#)');

export type PasswordRequirements = z.infer<typeof PasswordRequirementsSchema>;

/**
 * Login Request Schema
 *
 * Validates login payload from the frontend
 */
export const LoginRequestSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
});

export type LoginRequest = z.infer<typeof LoginRequestSchema>;

/**
 * Login Response Schema (Session)
 *
 * Returned after successful authentication
 */
export const LoginResponseSchema = z.object({
  user: z.object({
    id: z.string().uuid(),
    username: z.string(),
    displayName: z.string(),
    role: UserRoleSchema,
    locale: z.string(),
  }),
  expiresAt: z.string().datetime(),
  lockRemaining: z.number().int().nonnegative().optional(), // Seconds remaining if locked
});

export type LoginResponse = z.infer<typeof LoginResponseSchema>;

/**
 * Session Schema
 *
 * Tracks active user sessions for non-SSO authentication
 */
export const SessionSchema = z.object({
  sessionId: z.string().uuid(),
  userId: z.string().uuid(),
  createdAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
  lastSeenAt: z.string().datetime(),
});

export type Session = z.infer<typeof SessionSchema>;

/**
 * Password Change Request Schema
 */
export const PasswordChangeRequestSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: PasswordRequirementsSchema,
});

export type PasswordChangeRequest = z.infer<typeof PasswordChangeRequestSchema>;

/**
 * User Create Request Schema
 *
 * For creating new local users (admin function)
 */
export const UserCreateRequestSchema = z
  .object({
    site: SiteSchema,
    areaId: z.string().uuid('Invalid area ID').nullable().optional(),
    username: z
      .string()
      .min(3, 'Username must be at least 3 characters')
      .max(50, 'Username must not exceed 50 characters')
      .regex(
        /^[a-z0-9._-]+$/i,
        'Username can only contain letters, numbers, dots, hyphens, and underscores',
      ),
    displayName: z.string().min(1, 'Display name is required'),
    email: z.string().email('Invalid email').nullable().optional(),
    role: UserRoleSchema,
    globalRoles: GlobalRolesSchema.optional(),
    authProvider: AuthProviderSchema,
    m365ObjectId: z.string().uuid().nullable().optional(),
    m365Upn: z.string().email('Invalid email').nullable().optional(),
    password: PasswordRequirementsSchema.optional(), // Required for authProvider='local'
    locale: z.string().default('de-CH'),
    timeZone: z.string().default('Europe/Zurich'),
  })
  .superRefine((user, ctx) => {
    applyProviderBoundaryRules(user, ctx, {
      disallowedM365Credential: {
        present: user.password,
        path: 'password',
        message: 'Microsoft 365 users must not include password',
      },
    });
  });

export type UserCreateRequest = z.infer<typeof UserCreateRequestSchema>;

/**
 * Lockout Configuration Constants
 */
export const LOCKOUT_CONFIG = {
  MAX_FAILED_ATTEMPTS: 5, // Lock account after 5 failed login attempts
  LOCKOUT_DURATION_MINUTES: 15, // Lock duration in minutes
  SESSION_TIMEOUT_MINUTES: 30, // Session expires after 30 minutes of inactivity
} as const;

/**
 * Helper function to check if a user account is currently locked
 *
 * @param user - The user to check
 * @returns True if the user is locked, false otherwise
 */
export function isUserLocked(user: Pick<User, 'lockedUntil'>): boolean {
  if (!user.lockedUntil) {
    return false;
  }

  const lockoutExpiry = new Date(user.lockedUntil);
  const now = new Date();

  return now < lockoutExpiry;
}

/**
 * Helper function to calculate remaining lockout time in seconds
 *
 * @param user - The user to check
 * @returns Remaining lockout time in seconds, or 0 if not locked
 */
export function getRemainingLockoutSeconds(user: Pick<User, 'lockedUntil'>): number {
  if (!user.lockedUntil) {
    return 0;
  }

  const lockoutExpiry = new Date(user.lockedUntil);
  const now = new Date();

  if (now >= lockoutExpiry) {
    return 0;
  }

  return Math.ceil((lockoutExpiry.getTime() - now.getTime()) / 1000);
}

/**
 * Helper function to determine if user should be locked after failed attempt
 *
 * @param failedCount - Current failed login count
 * @returns True if user should be locked
 */
export function shouldLockUser(failedCount: number): boolean {
  return failedCount >= LOCKOUT_CONFIG.MAX_FAILED_ATTEMPTS;
}

/**
 * Helper function to calculate lockout expiry timestamp
 *
 * @returns ISO 8601 datetime string for lockout expiry
 */
export function calculateLockoutExpiry(): string {
  const expiry = new Date();
  expiry.setMinutes(expiry.getMinutes() + LOCKOUT_CONFIG.LOCKOUT_DURATION_MINUTES);
  return expiry.toISOString();
}
